import React, {useMemo} from 'react';

import {differenceInDays, format, isValid, parseISO} from 'date-fns';

import {useTranslation} from 'react-i18next';

import {doFetchPaymentInvoice} from '@/entities/subscriptions/model';
import {SubscriptionStatus, SubscriptionStatusEnum} from '@/entities/subscriptions/types';

import {Button, ButtonVariant} from '@/shared/ui/Button';

import {Icon} from '@/shared/ui/Icon';

import {PaywallPage} from '../Paywall';

import * as S from './styled';

type ActiveSubscriptionPageProps = {
    status: SubscriptionStatus;
};

const formatDate = (iso: string | undefined) => {
    if (!iso) return '—';
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'dd.MM.yyyy') : iso;
};
export const ActiveSubscriptionPage = ({status}: ActiveSubscriptionPageProps) => {
    const {t} = useTranslation();
    const {
        activeFrom,
        activeTo,
        remainingInvoices,
        invoiceLimit,
        status: subscriptionStatus,
    } = status;

    const planDays = useMemo(() => {
        if (!activeFrom || !activeTo) return null;
        const from = parseISO(activeFrom);
        const to = parseISO(activeTo);
        if (!isValid(from) || !isValid(to)) return null;
        const days = differenceInDays(to, from);
        return days >= 0 ? days : null;
    }, [activeFrom, activeTo]);

    const progressPct =
        invoiceLimit != null && invoiceLimit > 0 && remainingInvoices != null
            ? Math.min(100, Math.round(((invoiceLimit - remainingInvoices) / invoiceLimit) * 100))
            : 0;

    const fromStr = formatDate(activeFrom);
    const toStr = formatDate(activeTo);

    const showForCanceledStatus = useMemo(
        () => subscriptionStatus === SubscriptionStatusEnum.CANCELLED,
        [subscriptionStatus],
    );

    return (
        <S.Wrapper>
            <S.Title>{t('subscriptions.linkName')}</S.Title>

            <S.Card>
                <S.CardHeader>
                    <S.CardHeaderContentWrapper>
                        <S.CardHeaderContent>
                            <S.SubscriptionRow>
                                <S.SubscriptionLabel>
                                    {t('subscriptions.yourSubscription')}
                                </S.SubscriptionLabel>
                                <S.ActiveBadge $status={subscriptionStatus}>
                                    {t(`subscriptions.status.${subscriptionStatus}`)}
                                </S.ActiveBadge>
                            </S.SubscriptionRow>
                            {subscriptionStatus === SubscriptionStatusEnum.PAYMENT_PENDING && (
                                <S.SubscriptionRow>
                                    <S.RowLabel fullWidth>
                                        {t('subscriptions.completePayment')}
                                    </S.RowLabel>
                                </S.SubscriptionRow>
                            )}
                        </S.CardHeaderContent>
                        <S.CardHeaderControl>
                            {subscriptionStatus === SubscriptionStatusEnum.PAYMENT_PENDING && (
                                <div>
                                    <Button
                                        variant={ButtonVariant.Outline}
                                        onClick={() => doFetchPaymentInvoice()}
                                    >
                                        <Icon
                                            name="download2"
                                            width={16}
                                            height={16}
                                            style={{marginRight: 8}}
                                        />
                                        <span>{t('subscriptions.button.downloadInvoice')}</span>
                                    </Button>
                                </div>
                            )}
                        </S.CardHeaderControl>
                    </S.CardHeaderContentWrapper>
                    <S.HeaderDivider />
                </S.CardHeader>

                {!showForCanceledStatus && (
                    <S.Row noBorder>
                        <S.RowLabel>{t('subscriptions.plan')}</S.RowLabel>

                        <S.RowValue>
                            <S.PlanChips>
                                {planDays != null && (
                                    <S.PlanChipPrimary>
                                        {t('subscriptions.days', {days: planDays})}
                                    </S.PlanChipPrimary>
                                )}
                                {invoiceLimit != null &&
                                    invoiceLimit > 0 &&
                                    (subscriptionStatus ===
                                    SubscriptionStatusEnum.PAYMENT_PENDING ? (
                                        <S.PlanChipPending>
                                            {t('subscriptions.invoicesLeft', {
                                                invoices: invoiceLimit,
                                            })}
                                        </S.PlanChipPending>
                                    ) : (
                                        <S.PlanChipSoft>
                                            {t('subscriptions.invoicesLeft', {
                                                invoices: invoiceLimit,
                                            })}
                                        </S.PlanChipSoft>
                                    ))}
                                {planDays == null &&
                                    (invoiceLimit == null || invoiceLimit <= 0) && (
                                        <S.PlanChipSoft>—</S.PlanChipSoft>
                                    )}
                            </S.PlanChips>
                        </S.RowValue>
                    </S.Row>
                )}

                {!showForCanceledStatus && (
                    <S.Row noBorder>
                        <S.RowLabel>{t('subscriptions.periodOfValidity')}</S.RowLabel>
                        {subscriptionStatus !== SubscriptionStatusEnum.PAYMENT_PENDING ? (
                            <S.RowValue>
                                <S.PeriodText>
                                    {t('subscriptions.from')} <S.PeriodDate>{fromStr}</S.PeriodDate>{' '}
                                    {t('subscriptions.to')} <S.PeriodDate>{toStr}</S.PeriodDate>
                                </S.PeriodText>
                            </S.RowValue>
                        ) : (
                            <S.RowValue>
                                <S.PeriodText>{t('subscriptions.paymentPending')}</S.PeriodText>
                            </S.RowValue>
                        )}
                    </S.Row>
                )}

                {!showForCanceledStatus && (
                    <S.Row>
                        <S.RowLabel>{t('subscriptions.balance')}</S.RowLabel>

                        <S.BalanceBlock>
                            {invoiceLimit != null &&
                            invoiceLimit > 0 &&
                            remainingInvoices != null ? (
                                <>
                                    <S.BalanceHeadline>
                                        <S.BalanceHeadLeft>
                                            {t('subscriptions.invoices')}
                                        </S.BalanceHeadLeft>
                                        <S.BalanceHeadRight>
                                            <S.BalanceHeadEm>{remainingInvoices}</S.BalanceHeadEm>{' '}
                                            {t('subscriptions.invoicesLeftFrom')}{' '}
                                            <S.BalanceHeadEm>{invoiceLimit}</S.BalanceHeadEm>
                                        </S.BalanceHeadRight>
                                    </S.BalanceHeadline>
                                    <S.ProgressTrack>
                                        <S.ProgressFill $pct={progressPct} />
                                    </S.ProgressTrack>
                                    <S.LegendRow>
                                        <S.LegendItem>
                                            <S.LegendDot $variant="primary" />
                                            <span>
                                                {t('subscriptions.used')}{' '}
                                                <S.LegendStrong>
                                                    {invoiceLimit - remainingInvoices}
                                                </S.LegendStrong>{' '}
                                                {t('subscriptions.invoices')}
                                            </span>
                                        </S.LegendItem>
                                        <S.LegendItem>
                                            <S.LegendDot $variant="muted" />
                                            <span>
                                                {t('subscriptions.left')}{' '}
                                                <S.LegendStrong>{remainingInvoices}</S.LegendStrong>{' '}
                                                {t('subscriptions.invoices')}
                                            </span>
                                        </S.LegendItem>
                                    </S.LegendRow>
                                </>
                            ) : (
                                <S.FallbackText>
                                    {t('subscriptions.invoices')} {invoiceLimit}
                                </S.FallbackText>
                            )}
                        </S.BalanceBlock>
                    </S.Row>
                )}

                {showForCanceledStatus && (
                    <>
                        <S.Row noBorder>
                            <S.RowLabel fullWidth>{t('subscriptions.canceled')}</S.RowLabel>
                        </S.Row>
                        <S.Row noBorder>
                            <S.RowLabel fullWidth>
                                <S.EmailLink type="email" href="mailto:support@example.com">
                                    {t('subscriptions.contactSupport')}
                                </S.EmailLink>
                            </S.RowLabel>
                        </S.Row>
                    </>
                )}
            </S.Card>

            <PaywallPage />

            {/* {subscriptionStatus === SubscriptionStatusEnum.PAYMENT_PENDING && (
                <S.Footer>
                    <div>
                        <Button
                            variant={ButtonVariant.Outline}
                            onClick={() => cancelSubscription()}
                        >
                            <span>{t('subscriptions.button.cancelPlan')}</span>
                        </Button>
                    </div>
                </S.Footer>
            )} */}
        </S.Wrapper>
    );
};
